import { useCallback, useEffect, useMemo, useState } from 'react';

import { getBorrowedCards, getCollectionLinks } from '../services/collectionLinks';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { seedCollectionIdentities } from './useCollectionIdentity';
import { borrowedFromOf } from '../utils/linkedRecords';
import { normalizeId } from '../utils/ids';
import { getSessionKey, onSessionChange } from '../utils/session';

/**
 * WHAT THIS COLLECTION READS FROM OTHERS (keepr-api § Collection links,
 * § Merged lists): its live incoming links and the cards they lend it.
 *
 *   {
 *     incoming:      the incoming link rows (GET …/links `incoming`), live sources only
 *     borrowedCards: A's cards the links lend (…/cards?includeLinked=1), each with `borrowedFrom`
 *     sources:       Map(sourceId → { source, links, allowCopies, cardIds: Set })
 *     hasIncoming:   whether anything is read from elsewhere
 *     loading, refresh()
 *   }
 *
 * One read per collection per page load, shared: every lookup input on a
 * form asks the same question, so the answer is cached here by collection id
 * (and dropped by `refresh`). A reader the links list refuses — a card-scoped
 * or filtered grantee of B, who reads nothing through a link — gets the empty
 * answer, which is the truth for them. The borrowed cards also land in the
 * app-wide card cache, so a linked row's fields and a lookup into A render.
 *
 * The cache is PER SESSION (utils/session.js): the app signs out without a
 * reload, and the next account on this tab must never be served the last
 * one's links — or the borrowed card definitions of a collection it cannot
 * read. Every entry is stamped with the session it was read under and served
 * to that session only, an answer that lands after the account changed is
 * dropped, and a session change clears the lot.
 */
const cache = new Map(); // collectionId -> { at, owner, promise: Promise<{ incoming, borrowedCards }> }
// A link made or ended by someone else reaches a long-lived tab within a
// minute; one made here drops the entry at once (invalidateLinkedSources).
const TTL_MS = 60 * 1000;

const EMPTY = Object.freeze({ incoming: [], borrowedCards: [] });

onSessionChange(() => { cache.clear(); });

function load(collectionId) {
	const owner = getSessionKey();
	const held = cache.get(collectionId);
	if (held && (held.owner !== owner || Date.now() - held.at > TTL_MS)) cache.delete(collectionId);
	if (!cache.has(collectionId)) {
		const entry = { at: Date.now(), owner, promise: null };
		entry.promise = Promise.all([
			getCollectionLinks(collectionId).then(view => view.incoming || []).catch(() => []),
			getBorrowedCards(collectionId).catch(() => []),
		]).then(([incoming, borrowedCards]) => {
			// Signed out (or in as someone else) while this was in flight:
			// the answer is the previous account's, and nobody may see it.
			if (getSessionKey() !== owner) {
				if (cache.get(collectionId) === entry) cache.delete(collectionId);
				return EMPTY;
			}
			return {
				incoming: incoming.filter(link => link && !link.sourceDeleted && link.source),
				borrowedCards,
			};
		});
		cache.set(collectionId, entry);
	}
	return cache.get(collectionId).promise;
}

/** Forget what was read (a link was made, ended or changed). No id: everything. */
export function invalidateLinkedSources(collectionId = null) {
	if (collectionId) cache.delete(normalizeId(collectionId));
	else cache.clear();
}

/** The per-source summary: which links, whether any of them allows copies, which cards it lends. */
export function summarizeSources(incoming = [], borrowedCards = []) {
	const sources = new Map();
	const entry = (id, source) => {
		if (!sources.has(id)) sources.set(id, { source, links: [], allowCopies: false, cardIds: new Set() });
		return sources.get(id);
	};
	(incoming || []).forEach(link => {
		const id = normalizeId(link?.sourceId) || normalizeId(link?.source?._id);
		if (!id) return;
		const row = entry(id, link.source || null);
		row.links.push(link);
		if (link.allowCopies === true) row.allowCopies = true;
	});
	(borrowedCards || []).forEach(card => {
		const from = borrowedFromOf(card);
		const cardId = normalizeId(card?._id);
		if (!from || !cardId) return;
		const row = entry(from.collectionId, { _id: from.collectionId, name: from.named ? from.name : null, icon: from.icon, color: from.color });
		row.cardIds.add(cardId);
	});
	return sources;
}

export default function useLinkedSources(collectionId, { enabled = true } = {}) {
	const id = normalizeId(collectionId);
	const cardCache = useOptionalCardDefinitions();
	const addCards = cardCache?.addCards;
	const [state, setState] = useState({ key: null, data: EMPTY });
	const [nonce, setNonce] = useState(0);
	// The session is part of what was read: an answer held for the last
	// account is not this one's, even in a component that stayed mounted.
	const session = getSessionKey();
	const key = id ? `${session}|${id}` : null;

	useEffect(() => {
		if (!enabled || !id) return undefined;
		let live = true;
		load(id).then(data => {
			if (!live || getSessionKey() !== session) return;
			// What each source IS, for every badge that qualifies one of its
			// records (hooks/useCollectionIdentity).
			// Only a source the answer names: a nameless one is not an identity.
			seedCollectionIdentities([
				...data.incoming.map(link => link.source),
				...data.borrowedCards.map(card => card?.borrowedFrom?.collection),
			], { session });
			setState({ key: `${session}|${id}`, data });
			if (data.borrowedCards.length && typeof addCards === 'function') addCards(data.borrowedCards);
		});
		return () => { live = false; };
	}, [enabled, id, session, nonce, addCards]);

	const refresh = useCallback(() => {
		if (!id) return;
		cache.delete(id);
		setNonce(n => n + 1);
	}, [id]);

	const data = state.key === key ? state.data : EMPTY;
	const sources = useMemo(() => summarizeSources(data.incoming, data.borrowedCards), [data]);
	return {
		incoming: data.incoming,
		borrowedCards: data.borrowedCards,
		sources,
		hasIncoming: data.incoming.length > 0 || data.borrowedCards.length > 0,
		loading: Boolean(enabled && id) && state.key !== key,
		refresh,
	};
}
